import { Avatar, Flex, Group, Paper, Stack, Text } from '@mantine/core';

import TwoFA from './TwoFA';
import PasswordChange from './PasswordChange';
import Message from '@/components/content/message/Message';

import { useMessage } from '@/hooks/useMessage';
import { useAuth } from '@/hooks/useAuth';
import { useMSSP } from '@/hooks/useIsMSSP';
import { formatUserRole } from '../../utils/formatUserRole';

import FinanceIcon from '@/assets/icons/content/finance.svg';
import EmailIcon from '@/assets/icons/content/email.svg';
import LockIcon from '@/assets/icons/content/lock.svg';
import RadioSelectedIcon from '@/assets/icons/content/radio-selected.svg';

import classes from './ProfileInfo.module.css';

const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

const StatusBadge = ({
  tone = 'muted',
  children,
}: {
  tone?: 'success' | 'muted';
  children: string;
}) => (
  <Group
    gap={6}
    wrap="nowrap"
    className={`${classes.statusBadge} ${tone === 'muted' ? classes.statusInactive : ''}`}
  >
    <RadioSelectedIcon className={classes.statusIcon} aria-hidden="true" />
    <Text span fw={600} fz={14} lh="22px" className={classes.statusLabel}>
      {children}
    </Text>
  </Group>
);

const ProfileInfo = () => {
  const { createMessage, removeMessage, message } = useMessage();
  const { userInfo, tokenInfo } = useAuth();
  const { isMSSP } = useMSSP();

  const userName =
    [tokenInfo.first_name, tokenInfo.last_name].filter((value) => value?.trim()).join(' ') ||
    tokenInfo.sub;
  const userInitials = getInitials(userName) || getInitials(tokenInfo.client_name);
  const roleLabel = formatUserRole(tokenInfo.role);
  const tfaEnabled = Boolean(userInfo.twofa_activated);
  const samlEnabled = userInfo.external_auth === 1;
  const showTwoFa = !isMSSP && !samlEnabled;

  return (
    <Stack gap={20}>
      <Message message={message} removeMessage={removeMessage} closeButton />

      <Flex className={classes.cardsRow} gap={16} align="stretch" wrap="nowrap">
        <Paper
          withBorder
          shadow="none"
          radius={2}
          p={16}
          className={`${classes.card} ${classes.equalCard}`}
        >
          <Group gap={26} align="flex-start" wrap="nowrap">
            <Avatar size={114} radius={114} className={classes.avatar}>
              {userInitials}
            </Avatar>
            <Stack gap={16} className={classes.userDetails}>
              <Text className={classes.userName}>{userName}</Text>
              <Group gap={6} wrap="nowrap" className={classes.infoRow}>
                <FinanceIcon aria-hidden="true" />
                <Text span>{tokenInfo.client_name}</Text>
              </Group>
              <Group gap={6} wrap="nowrap" className={classes.infoRow}>
                <EmailIcon aria-hidden="true" />
                <Text span>{tokenInfo.sub}</Text>
              </Group>
              <Group gap={6} wrap="nowrap" className={classes.infoRow}>
                <LockIcon aria-hidden="true" />
                <PasswordChange createMessage={createMessage} className={classes.passwordLink} />
              </Group>
            </Stack>
          </Group>
        </Paper>

        <Paper
          withBorder
          shadow="none"
          radius={2}
          p={16}
          className={`${classes.card} ${classes.equalCard}`}
        >
          <Stack gap={20}>
            <Text className={`${classes.cardTitle} ${classes.cardTitleLg}`}>Security</Text>
            <Stack gap={8}>
              <Group gap={10} wrap="nowrap" className={classes.settingRow}>
                <Text span className={classes.settingLabel}>
                  Status
                </Text>
                <StatusBadge tone="success">Active</StatusBadge>
              </Group>
              {showTwoFa && (
                <Group gap={10} wrap="nowrap" className={classes.settingRow}>
                  <Text span className={classes.settingLabel}>
                    2FA
                  </Text>
                  <TwoFA enabled={tfaEnabled} createMessage={createMessage} />
                </Group>
              )}
              <Group gap={10} wrap="nowrap" className={classes.settingRow}>
                <Text span className={classes.settingLabel}>
                  SAML
                </Text>
                <StatusBadge tone={samlEnabled ? 'success' : 'muted'}>
                  {samlEnabled ? 'Enabled' : 'Disabled'}
                </StatusBadge>
              </Group>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          withBorder
          shadow="none"
          radius={2}
          p={16}
          className={`${classes.card} ${classes.equalCard}`}
        >
          <Stack gap={20}>
            <Text className={`${classes.cardTitle} ${classes.cardTitleLg}`}>Preferences</Text>
            <Stack gap={8}>
              <Group gap={10} wrap="nowrap" className={classes.prefRow}>
                <Text span className={classes.settingLabel}>
                  Language
                </Text>
                <Text span className={classes.keywordChip}>
                  English
                </Text>
              </Group>
              <Group gap={10} wrap="nowrap" className={classes.prefRow}>
                <Text span className={classes.settingLabel}>
                  Your company role
                </Text>
                <Text span className={classes.keywordChip}>
                  {roleLabel}
                </Text>
              </Group>
            </Stack>
          </Stack>
        </Paper>
      </Flex>
    </Stack>
  );
};

export default ProfileInfo;
